import type { UseFormReset } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'
import type { Message } from '@/interfaces/message'
import { listTicketMessagesQuery } from '@/queries/ticket.query'
import { TicketService } from '@/services/ticket.service'
import { toast } from 'sonner'
import {
  useChatStore,
  selectAddPendingMessage,
  selectMarkMessageFailed,
  selectMarkMessageSent,
} from '@/stores/chat.store'
import type { AttachmentPreview, FormValues } from './types'
import {
  buildOptimisticMessages,
  buildOptimisticRecordedAudioMessage,
} from './utils'

type UseMessageSubmitProps = {
  ticketId: number
  contactId: number
  whatsappId: string
  attachments: AttachmentPreview[]
  clearAttachments: () => void
  revokeAttachmentPreviews: (items: AttachmentPreview[]) => void
  reset: UseFormReset<FormValues>
}

export function useMessageSubmit({
  ticketId,
  contactId,
  whatsappId,
  attachments,
  clearAttachments,
  revokeAttachmentPreviews,
  reset,
}: UseMessageSubmitProps) {
  const queryClient = useQueryClient()
  const addPendingMessage = useChatStore(selectAddPendingMessage)
  const markMessageFailed = useChatStore(selectMarkMessageFailed)
  const markMessageSent = useChatStore(selectMarkMessageSent)
  const queryKey = listTicketMessagesQuery(ticketId).queryKey

  const replaceTempMessage = (tempId: string, response: Message) => {
    queryClient.setQueryData<Message[]>(queryKey, (old = []) => {
      const withoutTemp = old.filter((m) => m.id !== tempId)
      const alreadyExists = withoutTemp.some((m) => m.id === response.id)
      if (alreadyExists) return withoutTemp
      return [...withoutTemp, response]
    })
  }

  const sendPendingFormData = async ({
    tempId,
    formdata,
    errorMessage,
    onSuccess,
  }: {
    tempId: string
    formdata: FormData
    errorMessage: string
    onSuccess?: (response: Message) => void
  }) => {
    addPendingMessage(tempId, ticketId, formdata)

    try {
      const response = await TicketService.sendMessage(ticketId, formdata)
      replaceTempMessage(tempId, response)
      markMessageSent(tempId)
      onSuccess?.(response)
      return true
    } catch {
      markMessageFailed(tempId)
      toast.error(errorMessage)
      return false
    }
  }

  const submitMessage = async (data: FormValues) => {
    const message = data.message.trim()
    const hasAttachments = attachments.length > 0

    if (!message && !hasAttachments) return

    const attachmentsSnapshot = [...attachments]
    const optimisticMessages = buildOptimisticMessages({
      attachments: attachmentsSnapshot,
      message,
      ticketId,
      contactId,
      whatsappId,
    })

    queryClient.setQueryData<Message[]>(queryKey, (old = []) => {
      const existingIds = new Set(old.map((m) => m.id))
      const newMessages = optimisticMessages.filter(
        (m) => !existingIds.has(m.id)
      )
      return [...old, ...newMessages]
    })

    reset()
    clearAttachments()

    const uploadPromises: Promise<boolean>[] = []

    if (hasAttachments) {
      for (let i = 0; i < attachmentsSnapshot.length; i++) {
        const attachment = attachmentsSnapshot[i]
        const formdata = new FormData()
        formdata.append('type', attachment.type)
        formdata.append('file', attachment.file)

        const tempId = optimisticMessages[i]?.id || `temp-${Date.now()}-${i}`
        uploadPromises.push(
          sendPendingFormData({
            tempId,
            formdata,
            errorMessage: 'Erro ao enviar mensagem',
          })
        )
      }
    }

    if (message && hasAttachments) {
      const formdata = new FormData()
      formdata.append('type', 'text')
      formdata.append('content', message)

      const tempId = optimisticMessages[attachmentsSnapshot.length].id
      uploadPromises.push(
        sendPendingFormData({
          tempId,
          formdata,
          errorMessage: 'Erro ao enviar mensagem',
        })
      )
    } else if (message && !hasAttachments) {
      const formdata = new FormData()
      formdata.append('type', 'text')
      formdata.append('content', message)

      const tempId = optimisticMessages[0].id
      uploadPromises.push(
        sendPendingFormData({
          tempId,
          formdata,
          errorMessage: 'Erro ao enviar mensagem',
        })
      )
    }

    try {
      await Promise.allSettled(uploadPromises)
    } finally {
      if (hasAttachments) {
        revokeAttachmentPreviews(attachmentsSnapshot)
      }
    }
  }

  const submitRecordedAudio = async (
    file: File,
    mimeType: string,
    durationSeconds = 0
  ) => {
    const tempId = `temp-audio-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`
    const previewUrl = URL.createObjectURL(file)
    const optimisticMessage = buildOptimisticRecordedAudioMessage({
      tempId,
      file,
      mimeType,
      previewUrl,
      durationSeconds,
      ticketId,
      contactId,
      whatsappId,
    })

    queryClient.setQueryData<Message[]>(queryKey, (old = []) => {
      const alreadyExists = old.some((message) => message.id === tempId)
      if (alreadyExists) return old
      return [...old, optimisticMessage]
    })

    const formdata = new FormData()
    formdata.append('type', 'ptt')
    formdata.append('file', file)

    await sendPendingFormData({
      tempId,
      formdata,
      errorMessage: 'Erro ao enviar áudio gravado',
      onSuccess: () => {
        URL.revokeObjectURL(previewUrl)
      },
    })
  }

  return {
    submitMessage,
    submitRecordedAudio,
  }
}
